<template>
  <div class="min-h-screen flex items-center justify-center bg-muted/30 py-12 px-4">
    <div class="w-full max-w-md space-y-6">

      <!-- Header -->
      <div class="text-center space-y-1.5">
        <h1 class="text-2xl font-semibold tracking-tight">Create your account</h1>
        <p class="text-sm text-muted-foreground">Join Applypa and land your next client</p>
      </div>

      <!-- Card -->
      <div class="rounded-xl border border-border bg-card shadow-sm">
        <div class="p-6 space-y-4">
          <form @submit.prevent="handleSignup" class="space-y-4">

            <!-- Name row -->
            <div class="grid grid-cols-2 gap-3">
              <div class="space-y-1.5">
                <label for="firstName" class="text-sm font-medium leading-none">First name</label>
                <input
                  v-model="firstName"
                  id="firstName"
                  type="text"
                  class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                  placeholder="Juan"
                  required
                  autocomplete="given-name"
                />
                <p v-if="errors.firstName" class="text-xs text-destructive">{{ errors.firstName }}</p>
              </div>
              <div class="space-y-1.5">
                <label for="lastName" class="text-sm font-medium leading-none">Last name</label>
                <input
                  v-model="lastName"
                  id="lastName"
                  type="text"
                  class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                  placeholder="dela Cruz"
                  required
                  autocomplete="family-name"
                />
                <p v-if="errors.lastName" class="text-xs text-destructive">{{ errors.lastName }}</p>
              </div>
            </div>

            <!-- Email -->
            <div class="space-y-1.5">
              <label for="email" class="text-sm font-medium leading-none">Email address</label>
              <input
                v-model="email"
                id="email"
                type="email"
                class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                placeholder="you@example.com"
                required
                autocomplete="email"
              />
              <p v-if="errors.email" class="text-xs text-destructive">{{ errors.email }}</p>
            </div>

            <!-- Password -->
            <div class="space-y-1.5">
              <label for="password" class="text-sm font-medium leading-none">Password</label>
              <div class="relative">
                <input
                  v-model="password"
                  id="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors pr-10"
                  placeholder="Min. 8 characters"
                  required
                  autocomplete="new-password"
                  @input="checkPasswordStrength"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                  tabindex="-1"
                >
                  <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" class="text-xs"></i>
                </button>
              </div>
              <!-- Strength bar -->
              <div v-if="password" class="h-1 w-full bg-muted rounded-full overflow-hidden">
                <div
                  class="h-full rounded-full transition-all duration-300"
                  :class="{
                    'bg-red-500 w-1/3': passwordStrength === 1,
                    'bg-amber-500 w-2/3': passwordStrength === 2,
                    'bg-green-500 w-full': passwordStrength === 3,
                  }"
                ></div>
              </div>
              <p v-if="errors.password" class="text-xs text-destructive">{{ errors.password }}</p>
              <p v-else-if="password" class="text-xs" :class="passwordStrengthTextClass">{{ passwordStrengthText }}</p>
            </div>

            <!-- Confirm password -->
            <div class="space-y-1.5">
              <label for="confirmPassword" class="text-sm font-medium leading-none">Confirm password</label>
              <div class="relative">
                <input
                  v-model="confirmPassword"
                  id="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors pr-10"
                  placeholder="Repeat your password"
                  required
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                  tabindex="-1"
                >
                  <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" class="text-xs"></i>
                </button>
              </div>
              <p v-if="errors.confirmPassword" class="text-xs text-destructive">{{ errors.confirmPassword }}</p>
            </div>

            <!-- Terms -->
            <div class="rounded-lg border border-border bg-muted/20 p-3">
              <div class="flex items-start gap-3">
                <input
                  v-model="agreeToTerms"
                  id="terms"
                  type="checkbox"
                  class="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-primary"
                  required
                />
                <div class="text-sm">
                  <label for="terms" class="font-medium text-foreground cursor-pointer">
                    I agree to the
                    <router-link to="/terms" class="text-primary hover:underline">Terms of Service</router-link>
                    and
                    <router-link to="/privacy" class="text-primary hover:underline">Privacy Policy</router-link>
                  </label>
                  <p class="text-xs text-muted-foreground mt-0.5">Your data is handled per our privacy policy.</p>
                </div>
              </div>
              <p v-if="errors.agreeToTerms" class="text-xs text-destructive mt-2">{{ errors.agreeToTerms }}</p>
            </div>

            <!-- Error -->
            <div v-if="signupError" class="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive flex items-start gap-2">
              <i class="fas fa-exclamation-circle mt-0.5 shrink-0"></i>
              <span>{{ signupError }}</span>
            </div>

            <!-- Submit -->
            <button
              type="submit"
              :disabled="loading"
              class="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              <i v-if="loading" class="fas fa-spinner fa-spin text-xs"></i>
              {{ loading ? 'Creating account...' : 'Get started free' }}
            </button>
          </form>

          <!-- Divider -->
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-border"></div>
            </div>
            <div class="relative flex justify-center text-xs uppercase">
              <span class="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>

          <!-- In-app browser warning (Facebook/Instagram WebView) -->
          <div v-if="isInAppBrowser" class="rounded-md bg-amber-500/10 border border-amber-500/30 p-3 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
            <i class="fas fa-exclamation-triangle mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"></i>
            <div>
              <p class="font-medium">Google sign-in is not supported here</p>
              <p class="mt-0.5 text-xs text-amber-700 dark:text-amber-300">You're using Facebook's browser. Please open this page in <strong>Chrome or Safari</strong> to sign in with Google.</p>
              <button
                type="button"
                @click="copyPageUrl"
                class="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 dark:text-amber-200 underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-100"
              >
                <i class="fas fa-copy text-xs"></i>
                {{ urlCopied ? 'Link copied!' : 'Copy link to open in Chrome' }}
              </button>
            </div>
          </div>

          <!-- Google -->
          <button
            type="button"
            @click="handleGoogleSignup"
            :disabled="googleLoading || isInAppBrowser"
            class="inline-flex w-full items-center justify-center gap-2.5 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <i v-if="googleLoading" class="fas fa-spinner fa-spin text-xs"></i>
            <i v-else class="fab fa-google text-red-500 text-sm"></i>
            {{ googleLoading ? 'Connecting...' : 'Continue with Google' }}
          </button>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-border bg-muted/20 rounded-b-xl">
          <p class="text-center text-sm text-muted-foreground">
            Already have an account?
            <router-link to="/login" class="font-medium text-primary hover:underline">Sign in</router-link>
          </p>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <Toast
      :show="toast.show"
      :type="toast.type"
      :title="toast.title"
      :message="toast.message"
      @close="toast.show = false"
    />
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import apiClient from '@/services/api';
import { getInviteCode, clearInviteCode } from '@/utils/inviteLink';
import { startGoogleOAuth } from '@/utils/googleOAuth';
import authStore from '@/store/auth'; // Import auth store for updating login state
import Toast from '@/components/Toast.vue'; // Import Toast component

export default {
  name: 'SignupPage',
  components: {
    Toast
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    // Form fields
    const firstName = ref('');
    const lastName = ref('');
    const username = ref('');
    const email = ref('');
    const password = ref('');
    const confirmPassword = ref('');
    const accountType = ref('jobseeker');
    const agreeToTerms = ref(false);
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);

    // Detect Facebook/Instagram in-app browser (blocks Google OAuth)
    const isInAppBrowser = /FBAN|FBAV|FB_IAB|Instagram|fban|fbav/i.test(navigator.userAgent || '');
    const urlCopied = ref(false);
    const copyPageUrl = () => {
      navigator.clipboard.writeText(window.location.href).then(() => {
        urlCopied.value = true;
        setTimeout(() => { urlCopied.value = false; }, 3000);
      });
    };

    // Password strength
    const passwordStrength = ref(0); // 0: none, 1: weak, 2: medium, 3: strong

    // Password strength check
    const checkPasswordStrength = () => {
      const pwd = password.value;

      if (!pwd) {
        passwordStrength.value = 0;
        return;
      }

      let strength = 0;

      // Length check
      if (pwd.length >= 8) strength += 1;

      // Contains letter check
      if (/[A-Za-z]/.test(pwd)) strength += 1;

      // Contains number check
      if (/[0-9]/.test(pwd)) strength += 1;

      // Contains special character check
      if (/[^A-Za-z0-9]/.test(pwd)) strength += 1;

      // Final score
      if (strength <= 2) {
        passwordStrength.value = 1; // Weak
      } else if (strength === 3) {
        passwordStrength.value = 2; // Medium
      } else {
        passwordStrength.value = 3; // Strong
      }
    };

    // Password strength indicators
    const passwordStrengthClass = computed(() => {
      switch (passwordStrength.value) {
        case 1: return 'strength-weak';
        case 2: return 'strength-medium';
        case 3: return 'strength-strong';
        default: return '';
      }
    });

    const passwordStrengthText = computed(() => {
      switch (passwordStrength.value) {
        case 1: return 'Weak - add more complexity';
        case 2: return 'Medium - add special characters';
        case 3: return 'Strong password';
        default: return '';
      }
    });

    const passwordStrengthTextClass = computed(() => {
      switch (passwordStrength.value) {
        case 1: return 'text-red-600';
        case 2: return 'text-amber-600';
        case 3: return 'text-green-600';
        default: return 'text-muted-foreground';
      }
    });

    const passwordStrengthIconClass = computed(() => {
      switch (passwordStrength.value) {
        case 1: return 'fas fa-exclamation-triangle text-red-600';
        case 2: return 'fas fa-shield-alt text-yellow-600';
        case 3: return 'fas fa-shield-alt text-green-600';
        default: return 'fas fa-info-circle text-muted-foreground';
      }
    });

    // Form state
    const loading = ref(false);
    const googleLoading = ref(false);
    const signupError = ref('');
    const errors = ref({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      accountType: '',
      agreeToTerms: '',
      username: ''
    });

    // Password strength validation function
    const isPasswordStrong = (pwd) => {
      // Password must be at least 8 characters and include at least one letter and one number
      return pwd.length >= 8 && /[A-Za-z]/.test(pwd) && /[0-9]/.test(pwd);
    };

    // Toast state
    const toast = reactive({
      show: false,
      type: 'success',
      title: '',
      message: ''
    });

    const showToast = (type, title, message) => {
      toast.type = type;
      toast.title = title;
      toast.message = message;
      toast.show = true;
      setTimeout(() => {
        toast.show = false;
      }, 5000);
    };

    // Google sign-up handler
    const handleGoogleSignup = async () => {
      googleLoading.value = true;

      try {
        startGoogleOAuth('register');
      } catch (error) {
        googleLoading.value = false;
        signupError.value = 'Failed to connect to Google. Please try again.';
      }
    };

    // Handle signup submission
    const handleSignup = async () => {
      // Reset errors
      signupError.value = '';
      Object.keys(errors.value).forEach(key => {
        errors.value[key] = '';
      });

      // Basic validation
      let isValid = true;

      if (!firstName.value.trim()) {
        errors.value.firstName = 'First name is required';
        isValid = false;
      }

      if (!lastName.value.trim()) {
        errors.value.lastName = 'Last name is required';
        isValid = false;
      }

      if (!email.value.trim()) {
        errors.value.email = 'Email address is required';
        isValid = false;
      } else if (!/\S+@\S+\.\S+/.test(email.value)) {
        errors.value.email = 'Please enter a valid email address';
        isValid = false;
      }

      if (!password.value) {
        errors.value.password = 'Password is required';
        isValid = false;
      } else if (!isPasswordStrong(password.value)) {
        errors.value.password = 'Password must be at least 8 characters and include a letter and a number';
        isValid = false;
      }

      if (!confirmPassword.value) {
        errors.value.confirmPassword = 'Please confirm your password';
        isValid = false;
      } else if (password.value !== confirmPassword.value) {
        errors.value.confirmPassword = 'Passwords do not match';
        isValid = false;
      }

      if (!accountType.value) {
        errors.value.accountType = 'Please select an account type';
        isValid = false;
      }

      if (!agreeToTerms.value) {
        errors.value.agreeToTerms = 'You must agree to the terms and conditions';
        isValid = false;
      }

      if (!isValid) return;

      // Submit form
      loading.value = true;

      try {
        username.value = (firstName.value.toLowerCase() + lastName.value.toLowerCase()).replace(/[^a-z0-9]/g, '') + Math.floor(Math.random() * 1000);
        const response = await apiClient.post('/auth/register/', {
          username: username.value,
          first_name: firstName.value,
          last_name: lastName.value,
          email: email.value,
          password: password.value,
          confirm_password: confirmPassword.value,
          account_type: 'jobseeker', // Always register as jobseeker
          invite: getInviteCode() || undefined, // campaign link, turns on OLJ jobs
        }, { timeout: 60000 });
        clearInviteCode();

        console.log('[Register] response.data:', response.data);
        console.log('[Register] user.email_verified:', response.data.user?.email_verified);

        // If registration is successful, redirect to login or verification page
        if (response.data.message === 'verification_required') {
          showToast('success', 'Verification Required', 'Please check your email to verify your account');
          // Redirect to verification page
          setTimeout(() => {
            router.push({
              name: 'VerifyEmailCode'
            });
          }, 2000);
        } else {
          // Registration with automatic login
          const token = response.data.token;

          if (token) {
            showToast('success', 'Account Created', 'Your account has been successfully created!');
            // Store token and set up headers for future API calls
            localStorage.setItem('token', token);
            apiClient.defaults.headers.common['Authorization'] = `Token ${token}`;

            // Update user info in auth store to update UI
            try {
              const userResponse = await apiClient.get('/auth/user/');
              // Update auth store with user data
              authStore.setAuth(true, userResponse.data);
            } catch (error) {
              // Error fetching user after registration
              authStore.setAuth(true, response.data.user);
            }

            // New users go to the onboarding wizard; existing redirect param takes priority
            const redirectTo = route.query.redirect || '/onboarding';
            const emailVerified = response.data.user?.email_verified !== false;
            console.log('[Register] emailVerified computed as:', emailVerified, '-> will navigate to', emailVerified ? redirectTo : 'VerifyEmailCode');

            setTimeout(() => {
              if (!emailVerified) {
                // Email verification is required before the rest of the app is reachable
                console.log('[Register] pushing to VerifyEmailCode with redirect =', redirectTo);
                router.push({ name: 'VerifyEmailCode', query: { redirect: redirectTo } });
              } else {
                console.log('[Register] pushing to', redirectTo);
                router.push(redirectTo);
              }
            }, 1500);
          } else {
            showToast('info', 'Registration Complete', 'Please log in with your new account');
            // No token, redirect to login after a short delay
            setTimeout(() => {
              router.push({
                name: 'Login',
                query: { registered: 'success' }
              });
            }, 1500);
          }
        }
      } catch (error) {
        if (error.response) {
          // Handle different error statuses
          switch (error.response.status) {
            case 400: {
              // Bad request - validation errors
              const responseData = error.response.data;

              // Map backend errors to form fields
              if (responseData.first_name) {
                errors.value.firstName = responseData.first_name[0];
              }
              if (responseData.last_name) {
                errors.value.lastName = responseData.last_name[0];
              }
              if (responseData.username) {
                errors.value.username = responseData.username[0];
              }
              if (responseData.email) {
                errors.value.email = responseData.email[0];
              }
              if (responseData.password) {
                errors.value.password = responseData.password[0];
              }
              if (responseData.account_type) {
                errors.value.accountType = responseData.account_type[0];
              }
              if (responseData.non_field_errors) {
                signupError.value = responseData.non_field_errors[0];
                showToast('error', 'Registration Error', responseData.non_field_errors[0]);
              }
              break;
            }
            case 401: {
              signupError.value = 'Authentication error. Please try again.';
              showToast('error', 'Authentication Error', 'Please try again.');
              break;
            }
            case 409: {
              if (error.response.data.email) {
                errors.value.email = 'This email is already registered';
                showToast('error', 'Registration Error', 'This email is already registered');
              }
              if (error.response.data.username) {
                errors.value.username = 'This username is already taken';
                showToast('error', 'Registration Error', 'This username is already taken');
              }
              break;
            }
            case 429: {
              signupError.value = 'Too many signup attempts. Please try again later.';
              showToast('error', 'Rate Limit', 'Too many signup attempts. Please try again later.');
              break;
            }
            default: {
              signupError.value = 'An error occurred. Please try again.';
              showToast('error', 'Registration Error', 'An error occurred. Please try again.');
              break;
            }
          }
        } else if (error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout'))) {
          signupError.value = 'Registration is taking longer than expected. Please wait a moment and check your email — your account may have been created.';
          showToast('error', 'Request Timed Out', 'Please check your email before trying again.');
        } else {
          signupError.value = 'Unable to connect to the server. Please check your internet connection.';
          showToast('error', 'Connection Error', 'Unable to connect to the server. Please check your internet connection.');
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      accountType,
      agreeToTerms,
      showPassword,
      showConfirmPassword,
      loading,
      googleLoading,
      signupError,
      errors,
      handleSignup,
      handleGoogleSignup,
      toast,
      // Password strength properties
      checkPasswordStrength,
      passwordStrength,
      passwordStrengthClass,
      passwordStrengthText,
      passwordStrengthTextClass,
      passwordStrengthIconClass,
      // Validate password strength (using checkPasswordStrength instead)
      isPasswordStrong,
      isInAppBrowser,
      urlCopied,
      copyPageUrl,
    };
  }
};
</script>
